import { motion } from "framer-motion";
import { 
  Sparkles, 
  Scale, 
  Network, 
  Shield, 
  Link2, 
  Eye 
} from "lucide-react";
import { PillarCard } from "@/components/ui/PillarCard";

const pillars = [
  {
    title: "Reality vs narrative",
    score: 78,
    icon: Sparkles,
    description: "Does the technology actually do what the sales deck claims? We verify whether 'proprietary AI' is real innovation or a thin wrapper around open-source.",
    issues: 4,
  },
  {
    title: "IP authenticity",
    score: 71,
    icon: Scale,
    description: "Is the intellectual property genuinely owned? We identify license contamination, third-party dependencies, and potential legal exposure.",
    issues: 8,
  },
  {
    title: "Architecture survivability",
    score: 85,
    icon: Network,
    description: "Will the platform survive the acquisition? We assess technical debt, integration complexity, and whether the architecture can scale post-merger.",
    issues: 12,
  },
  {
    title: "Security & abuse exposure",
    score: 68,
    icon: Shield,
    description: "What vulnerabilities exist? Beyond basic scanning, we evaluate attack surfaces, data handling practices, and compliance readiness.",
    issues: 23,
  },
  {
    title: "Dependency & fragility",
    score: 72,
    icon: Link2,
    description: "How fragile is the system? We map critical dependencies, vendor lock-in risks, and single points of failure that could impact operations.",
    issues: 15,
  },
  {
    title: "Reputational blast radius",
    score: 91,
    icon: Eye,
    description: "What hidden liabilities exist? We uncover dark patterns, ethical concerns, and practices that could damage your brand post-acquisition.",
    issues: 3,
  },
];

export const Pillars = () => {
  return (
    <section id="features" className="relative py-24">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/30 to-background" />

      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-cream font-medium text-sm uppercase tracking-wider">
            The Technology Reality Score™
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4 mb-6">
            Six pillars of truth
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Our proprietary framework cuts through marketing narratives to evaluate 
            what matters most in technology acquisitions. Each pillar is assessed by 
            our expert team and scored for investment readiness.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pillars.map((pillar, index) => (
            <PillarCard
              key={pillar.title}
              {...pillar}
              delay={index * 0.1}
            />
          ))}
        </div>

        {/* Score bands explanation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 rounded-2xl border border-border bg-card/50 p-8"
        >
          <h3 className="font-display text-xl font-semibold text-center mb-6">
            Understanding the score
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { range: "80-100", label: "Authentic & defensible", color: "bg-cream", desc: "Technology matches claims" },
              { range: "60-79", label: "Real but fragile", color: "bg-cream/70", desc: "Solid with known risks" },
              { range: "40-59", label: "Overstated", color: "bg-score-warning", desc: "Gap between claims & reality" },
              { range: "<40", label: "Narrative-driven risk", color: "bg-score-critical", desc: "Significant concerns" },
            ].map((band) => (
              <div key={band.range} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                <div className={`h-3 w-3 rounded-full ${band.color}`} />
                <div>
                  <span className="block text-sm font-semibold">{band.range}: {band.label}</span>
                  <span className="text-xs text-muted-foreground">{band.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
